import React, { useMemo } from 'react';
import { Box, Typography, Paper, Grid, Card, CardContent, useTheme } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { useGetCustomerReferencesQuery } from '../../../gql/generated/graphql';
import { getNodes } from '../../../gql/helpers';

interface CustomerOverviewProps {
  companyId: number;
}

// CI Color palette for charts
const CI_COLORS = [
  '#FF564F', // coral
  '#3F5D66', // green
  '#252F45', // dark blue
  '#334160', // medium dark blue
  '#4E6694', // medium blue
  '#7B90B9', // light blue
  '#B0BCD5', // very light blue
  '#67282D', // oxblood
  '#3A4248', // charcoal
  '#F6ECE1', // stoney
];

// Hardcoded SIC code descriptions map - temporary solution until backend query is fixed
const SIC_CODE_DESCRIPTIONS: { [key: string]: string } = {
  '2711': 'Newspapers: Publishing, or Publishing & Printing',
  '2721': 'Periodicals: Publishing, or Publishing & Printing',
  '2731': 'Books: Publishing, or Publishing & Printing',
  '2741': 'Miscellaneous Publishing',
  '3571': 'Electronic Computers',
  '3572': 'Computer Storage Devices',
  '3575': 'Computer Terminals',
  '3577': 'Computer Peripheral Equipment',
  '3578': 'Calculating & Accounting Machines',
  '3579': 'Office Machines',
  '3661': 'Telephone & Telegraph Apparatus',
  '3663': 'Radio & TV Broadcasting & Communications Equipment',
  '3669': 'Communications Equipment',
  '3674': 'Semiconductors & Related Devices',
  '3695': 'Magnetic & Optical Recording Media',
  '4812': 'Radiotelephone Communications',
  '4813': 'Telephone Communications',
  '4822': 'Telegraph & Other Message Communications',
  '4832': 'Radio Broadcasting Stations',
  '4833': 'Television Broadcasting Stations',
  '4841': 'Cable & Other Pay Television Services',
  '4899': 'Communications Services',
  '5044': 'Office Equipment',
  '5045': 'Computers & Computer Peripheral Equipment & Software',
  '5734': 'Computer & Computer Software Stores',
  '5961': 'Catalog & Mail-Order Houses',
  '7370': 'Computer Programming, Data Processing',
  '7371': 'Computer Programming Services',
  '7372': 'Prepackaged Software',
  '7373': 'Computer Integrated Systems Design',
  '7374': 'Computer Processing & Data Preparation',
  '7375': 'Information Retrieval Services',
  '7376': 'Computer Facilities Management Services',
  '7377': 'Computer Rental & Leasing',
  '7378': 'Computer Maintenance & Repair',
  '7379': 'Computer Related Services',
  '7812': 'Motion Picture & Video Tape Production',
  '7819': 'Services Allied to Motion Picture Production',
  '7822': 'Motion Picture & Video Tape Distribution',
  '7829': 'Services Allied to Motion Picture Distribution',
  '7832': 'Motion Picture Theaters',
  '8111': 'Legal Services',
  '8721': 'Accounting, Auditing & Bookkeeping Services',
  '8731': 'Commercial Physical & Biological Research',
  '8732': 'Commercial Economic, Sociological & Educational Research',
  '8733': 'Noncommercial Research Organizations',
  '8734': 'Testing Laboratories',
  '8741': 'Management Services',
  '8742': 'Management Consulting Services',
  '8743': 'Public Relations Services',
  '8744': 'Facilities Support Management Services',
  '8748': 'Business Consulting Services',
};

const CustomerOverview: React.FC<CustomerOverviewProps> = ({ companyId }) => {
  const theme = useTheme();
  const { data: customerReferences, loading: customerReferencesLoading } = useGetCustomerReferencesQuery({
    variables: { companyId },
  });

  const customerReferencesNodes = getNodes(customerReferences?.getCustomerReferences);

  const { pieChartData, barChartData, totalRevenue, customerCount, sicCodeDetails, waterfallData } = useMemo(() => {
    console.log('CustomerOverview - customerReferencesNodes:', customerReferencesNodes);

    if (!customerReferencesNodes || customerReferencesNodes.length === 0) {
      return {
        pieChartData: [],
        barChartData: { labels: [], data: [] },
        totalRevenue: 0,
        customerCount: 0,
        sicCodeDetails: [],
      };
    }

    const sicMap = new Map<string, { revenue: number; count: number; description?: string }>();
    let total = 0;
    let validCustomers = 0;

    customerReferencesNodes.forEach((customer: any) => {
      const sicCode = customer.sicCode;
      const shareOfRevenue = customer.shareOfRevenue || 0;

      // Count customers with revenue share
      if (shareOfRevenue > 0) {
        validCustomers++;
        total += shareOfRevenue;
      }

      // Group by SIC code (including null/empty as "Unknown")
      const sicKey = sicCode || 'Unknown';
      const existing = sicMap.get(sicKey) || { revenue: 0, count: 0 };
      const description = SIC_CODE_DESCRIPTIONS[sicCode] || existing.description;
      sicMap.set(sicKey, {
        revenue: existing.revenue + shareOfRevenue,
        count: existing.count + 1,
        description: description,
      });
    });

    console.log('CustomerOverview - sicMap:', Array.from(sicMap.entries()));

    // Helper function to abbreviate industry names
    const abbreviateIndustry = (description: string): string => {
      if (!description || description === 'Unknown') return description;

      // Common abbreviations
      const abbreviations: { [key: string]: string } = {
        Manufacturing: 'Mfg',
        Information: 'Info',
        Technology: 'Tech',
        Services: 'Svcs',
        Management: 'Mgmt',
        Professional: 'Prof',
        Technical: 'Tech',
        Scientific: 'Sci',
        Administrative: 'Admin',
        Support: 'Supp',
        'Waste Management': 'Waste Mgmt',
        Remediation: 'Remed',
        Transportation: 'Trans',
        Warehousing: 'Whse',
        Construction: 'Const',
        Wholesale: 'Whsle',
        Retail: 'Retail',
        Finance: 'Fin',
        Insurance: 'Ins',
        'Real Estate': 'RE',
        Educational: 'Edu',
        'Health Care': 'HC',
        'Social Assistance': 'Soc Asst',
        Entertainment: 'Ent',
        Recreation: 'Rec',
        Accommodation: 'Accom',
        'Food Services': 'Food Svcs',
      };

      let abbreviated = description;
      Object.entries(abbreviations).forEach(([full, abbr]) => {
        abbreviated = abbreviated.replace(new RegExp(full, 'gi'), abbr);
      });

      // If still too long, truncate
      if (abbreviated.length > 30) {
        abbreviated = abbreviated.substring(0, 27) + '...';
      }

      return abbreviated;
    };

    const sortedData = Array.from(sicMap.entries())
      .map(([sicCode, data]) => {
        const fullDescription = data.description || sicCode;
        const abbreviatedDescription = abbreviateIndustry(fullDescription);
        return {
          sicCode,
          value: parseFloat((data.revenue * 100).toFixed(2)),
          count: data.count,
          description: fullDescription,
          abbreviatedDescription: abbreviatedDescription,
          label: sicCode === 'Unknown' ? 'Unknown' : fullDescription === sicCode ? sicCode : abbreviatedDescription,
        };
      })
      .sort((a, b) => b.value - a.value)
      .slice(0, 10); // Top 10 SIC codes

    const pieData = sortedData.map((item, index) => ({
      id: index,
      value: item.value,
      label: item.label, // Use the label with description instead of just sicCode
    }));

    const barData = {
      labels: sortedData.map(item => (item.sicCode === 'Unknown' ? 'Unknown' : item.sicCode)),
      data: sortedData.map(item => item.value),
    };

    // Prepare waterfall chart data
    const customersSortedByRevenue = customerReferencesNodes
      .filter((customer: any) => customer.shareOfRevenue > 0)
      .sort((a: any, b: any) => b.shareOfRevenue - a.shareOfRevenue)
      .map((customer: any, index: number) => ({
        name: customer.customerReferenceName || customer.shortName || `Customer ${index + 1}`,
        value: customer.shareOfRevenue * 100,
        index: index + 1,
      }));

    // Calculate cumulative values for waterfall effect
    let cumulative = 0;
    const waterfallChartData = customersSortedByRevenue.map((customer, index) => {
      const startValue = cumulative;
      cumulative += customer.value;
      return {
        ...customer,
        startValue,
        endValue: cumulative,
        label: index < 20 ? `C${customer.index}` : '', // Only show labels for first 20
      };
    });

    // Add markers for top 5 and top 10
    const top5Value = customersSortedByRevenue.slice(0, 5).reduce((sum, c) => sum + c.value, 0);
    const top10Value = customersSortedByRevenue.slice(0, 10).reduce((sum, c) => sum + c.value, 0);

    // Add visual markers in the data for top 5 and top 10
    if (waterfallChartData.length >= 5) {
      waterfallChartData[4].isTop5 = true;
    }
    if (waterfallChartData.length >= 10) {
      waterfallChartData[9].isTop10 = true;
    }

    return {
      pieChartData: pieData,
      barChartData: barData,
      totalRevenue: total * 100,
      customerCount: validCustomers,
      sicCodeDetails: sortedData,
      waterfallData: {
        data: waterfallChartData,
        top5Value,
        top10Value,
        totalCustomers: customersSortedByRevenue.length,
      },
    };
  }, [customerReferencesNodes]);

  if (customerReferencesLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={400}>
        <Typography>Loading customer data...</Typography>
      </Box>
    );
  }

  if (!customerReferencesNodes || customerReferencesNodes.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={400}>
        <Typography color="text.secondary">No customer data available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Customer Revenue Distribution by Industry
      </Typography>

      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Customers
              </Typography>
              <Typography variant="h4">{customerReferencesNodes.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Customers with Revenue Data
              </Typography>
              <Typography variant="h4">{customerCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Unique Industries (SIC)
              </Typography>
              <Typography variant="h4">{sicCodeDetails.length}</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Pie Chart */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, height: 450 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Revenue Share by Industry
            </Typography>
            <Box sx={{ height: 300 }}>
              <PieChart
                series={[
                  {
                    data: pieChartData,
                    highlightScope: { faded: 'global', highlighted: 'item' },
                    faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                  },
                ]}
                colors={CI_COLORS}
                height={300}
                margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
                slotProps={{
                  legend: {
                    hidden: true,
                  },
                }}
                tooltip={{ trigger: 'item' }}
              />
            </Box>
            {/* Custom Legend */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                mt: 1,
                maxHeight: 120,
                overflow: 'auto',
              }}
            >
              {pieChartData.map((item, index) => {
                const column = Math.floor(index / 7);
                return (
                  <Box
                    key={item.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: 9,
                      mr: 2,
                      mb: 0.5,
                      minWidth: '120px',
                      width: pieChartData.length > 7 ? '140px' : 'auto',
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        backgroundColor: CI_COLORS[index % CI_COLORS.length],
                        borderRadius: '2px',
                        mr: 0.5,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 9,
                        lineHeight: 1.2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={item.label}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>

        {/* Bar Chart */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, height: 450 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Top Industries by Revenue
            </Typography>
            <Box sx={{ height: 350 }}>
              <BarChart
                xAxis={[
                  {
                    scaleType: 'band',
                    data: barChartData.labels,
                    tickLabelStyle: {
                      angle: 45,
                      textAnchor: 'start',
                    },
                  },
                ]}
                series={[
                  {
                    data: barChartData.data,
                    label: 'Revenue Share (%)',
                    color: CI_COLORS[0], // Use coral as primary
                  },
                ]}
                height={400}
                margin={{ top: 10, bottom: 140, left: 60, right: 10 }}
                slotProps={{
                  legend: {
                    position: { vertical: 'bottom', horizontal: 'middle' },
                    direction: 'row',
                  },
                }}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Customer Concentration Waterfall Chart */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, height: 450 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Customer Concentration
            </Typography>
            <Box sx={{ height: 350 }}>
              <BarChart
                xAxis={[
                  {
                    scaleType: 'band',
                    data: waterfallData.data.map(d => d.label),
                    tickLabelStyle: {
                      fontSize: 10,
                    },
                  },
                ]}
                yAxis={[
                  {
                    max: 100,
                  },
                ]}
                series={[
                  {
                    data: waterfallData.data.map(d => d.startValue),
                    label: 'Previous',
                    color: 'transparent',
                    stack: 'waterfall',
                  },
                  {
                    data: waterfallData.data.map(d => d.value),
                    label: 'Customer Share',
                    color: CI_COLORS[1], // Use green for waterfall
                    stack: 'waterfall',
                  },
                ]}
                height={380}
                margin={{ top: 10, bottom: 80, left: 60, right: 10 }}
                slotProps={{
                  legend: {
                    hidden: true,
                  },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', mt: -1 }}>
              <Typography variant="caption" color="text.secondary">
                Top 5: {waterfallData.top5Value.toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Top 10: {waterfallData.top10Value.toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total: {waterfallData.totalCustomers} customers
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Industry Details Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, height: 450, overflow: 'auto' }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Industry Breakdown Details
            </Typography>
            <Box sx={{ mt: 2, height: 380, overflow: 'auto' }}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <Typography variant="body2" fontWeight="bold">
                    SIC Code
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" fontWeight="bold">
                    Industry Description
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" fontWeight="bold">
                    Revenue Share
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography variant="body2" fontWeight="bold">
                    Customer Count
                  </Typography>
                </Grid>
              </Grid>
              {sicCodeDetails.map((item, index) => (
                <Grid
                  container
                  spacing={2}
                  key={item.sicCode}
                  sx={{ mt: 0.5, py: 1, bgcolor: index % 2 === 0 ? 'grey.50' : 'white' }}
                >
                  <Grid item xs={2}>
                    <Typography variant="body2">{item.sicCode}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" title={item.description}>
                      {item.abbreviatedDescription || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body2">{item.value.toFixed(2)}%</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body2">{item.count}</Typography>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerOverview;
